import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectPhase, selectPlayers, selectTurnOrder,} from '../../../redux/reducers/sharedStateReducer/selectors';
import {PHASES} from '../../../constants/phases';
import PageButton from '../../elements/PageButton/PageButton';
import {getPlayerHexColor} from '../../../utils/players/getPlayerHexColor';
import './SetupPlayersModal.css';
import FactionSkinIcon from '../../RankSkinFrame/FactionSkinIcon';
import RankSkinFrame from '../../RankSkinFrame/RankSkinFrame';
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import {getFactionConfig} from "../../../utils/factions/getFactionConfig";
import {getVisibleBotTitle} from '../../../utils/bots/getVisibleBotTitle';
import {checkIfBotMarkShown} from '../../../utils/bots/checkIfBotMarkShown';
import {getBotMarkAlt} from '../../../utils/bots/getBotMarkAlt';
import {
  getSetupPlayerBotIconPath,
  getSetupPlayerFactionImagePath,
  SETUP_PLAYERS_MODAL_INITIATIVE_ICON_PATH,
} from './getSetupPlayersModalImagePaths';

export const SETUP_PLAYER_MODAL_PLAYER_ANIMATION_DURATION_MS = 700;
export const SETUP_PLAYER_MODAL_DURATION_PER_PLAYER = 3000;
const SOUND_OFFSET_MS = 220;

const Faction_IMAGE_FADE_DURATION_MS = 2000;

// Пометка ботов включается ЯВНО и только настоящим матчем: в витрине главного
// меню (MenuDemoSetupIntro) идет чужая запись, и выдавать в ней ботов незачем —
// по умолчанию пометок нет
const SetupPlayersModal = ({
  onSkip, isAwaitingPlayers, title = 'Жребий брошен!', soundVolume, showVisibleBotMarks = false,
}) => {
  const phase = useSelector(selectPhase);
  const players = useSelector(selectPlayers);
  const turnOrder = useSelector(selectTurnOrder);
  const [revealedPlayerIds, setRevealedPlayerIds] = useState(new Set());

  const orderedPlayers = useMemo(() => {
    // isAwaitingPlayers — состав своего матча еще не пришел (первые кадры после
    // старта): в сторе пока лежит чужая партия, и ее игроков показывать нельзя
    if (isAwaitingPlayers || !players || !turnOrder || turnOrder.length === 0) {
      return [];
    }

    const playersById = new Map(players.map((player) => [player.id, player]));
    return turnOrder.map((playerId) => playersById.get(playerId)).filter(Boolean);
  }, [isAwaitingPlayers, players, turnOrder]);

  useEffect(() => {
    if (phase !== PHASES.SETUP || orderedPlayers.length === 0) {
      return;
    }

    const timers = orderedPlayers.map((player, index) => {
      const delayMs = index * SETUP_PLAYER_MODAL_DURATION_PER_PLAYER;
      const soundMs = delayMs + SETUP_PLAYER_MODAL_PLAYER_ANIMATION_DURATION_MS - SOUND_OFFSET_MS;

      return setTimeout(() => {
        // soundVolume задает витрина меню (свой ползунок); в матче его нет,
        // и звук идет по общей громкости настроек
        MENU_SOUND_MANAGER.playFactionSelect(player.factionId, false, soundVolume);
      }, soundMs);
    });

    return () => timers.forEach(clearTimeout);
  }, [orderedPlayers, phase, soundVolume]);

  if (phase !== PHASES.SETUP || (orderedPlayers.length === 0 && !isAwaitingPlayers)) {
    return null;
  }

  // Состав своего матча еще не пришел: занимаем экран одним фоном плашки —
  // иначе на ее месте успевал бы мелькнуть экран загрузки. Ни заголовка, ни
  // строк: они выравниваются по центру, и с приходом состава заголовок
  // прыгнул бы вверх
  if (isAwaitingPlayers) {
    return <div className="setup-players-modal"/>;
  }

  return (
    <>
      <div className="setup-players-modal">
        <h2 className="setup-players-modal-title">{title}</h2>
        <div className="setup-players-modal-list">
          {orderedPlayers.map((player, index) => {
            const factionConfig = getFactionConfig(player.factionId);
            const playerHexColor = getPlayerHexColor(player.color);

            return (
              <div
                key={player.id}
                className="setup-players-modal-player setup-players-modal-player_animated"
                style={{
                  backgroundColor: playerHexColor,
                  animationDelay: `${index * SETUP_PLAYER_MODAL_DURATION_PER_PLAYER}ms`,
                  '--faction-color': playerHexColor,
                }}
                onAnimationEnd={() => {
                  setRevealedPlayerIds((prev) => new Set(prev).add(player.id));
                  setTimeout(() => {
                    setRevealedPlayerIds((prev) => {
                      const next = new Set(prev);
                      next.delete(player.id);
                      return next;
                    });
                  }, Faction_IMAGE_FADE_DURATION_MS);
                }}
              >
                <FactionSkinIcon
                  imgClassName="setup-players-modal-faction-icon"
                  factionId={player.factionId}
                  skinId={player.selectedSkin}
                  alt={player.factionId}
                />
                <div className={'setup-players-modal-name-container'}>
                 <span
                   className="setup-players-modal-name"
                 >
                  {player.name}
                  {showVisibleBotMarks && checkIfBotMarkShown(player) && (
                    <img
                      className="setup-players-modal-bot-icon"
                      src={getSetupPlayerBotIconPath(player)}
                      alt={getBotMarkAlt(player)}
                      title={getVisibleBotTitle(player)}
                    />
                  )}
                </span>
                  <span
                    className="setup-players-modal-faction-name"
                  >
                  {factionConfig.name}
                </span>
                </div>
                {index === 0 && (
                  <img
                    className="setup-players-modal-initiative-icon"
                    src={SETUP_PLAYERS_MODAL_INITIATIVE_ICON_PATH}
                    alt="инициатива"
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="setup-players-modal-faction-images">
          {orderedPlayers
            .filter((player) => revealedPlayerIds.has(player.id))
            .map((player) => {
              const factionConfig = getFactionConfig(player.factionId);
              // Путь — той же функцией, по которой витрина меню прогружает
              // картинки до начала анимации (getSetupPlayersModalImagePaths)
              const imagePath = getSetupPlayerFactionImagePath(player);
              const playerHexColor = getPlayerHexColor(player.color);

              return (
                <div
                  key={player.id}
                  className="setup-players-modal-faction-image-wrapper"
                  style={{color: playerHexColor}}
                >
                  <div className="setup-players-modal-faction-image-overlay"/>
                  <span className="setup-players-modal-faction-image-player-name">
                    {player.name}
                    {showVisibleBotMarks && checkIfBotMarkShown(player) && (
                      <img
                        className="setup-players-modal-bot-icon"
                        src={getSetupPlayerBotIconPath(player)}
                        alt={getBotMarkAlt(player)}
                        title={getVisibleBotTitle(player)}
                      />
                    )}
                  </span>
                  {/* Бокс нужен рамке рейтингового образа: она ложится ровно
                      по картинке, а растушевка краев остается на самой
                      картинке и рамку не трогает (см. RankSkinFrame) */}
                  <span className="setup-players-modal-faction-image-box">
                    <img
                      className="setup-players-modal-faction-image"
                      src={imagePath}
                      alt={player.factionId}
                      style={{
                        background: playerHexColor,
                      }}
                    />
                    <RankSkinFrame
                      skinId={player.selectedSkin}
                      className="setup-players-modal-faction-frame"
                    />
                  </span>
                  <span className="setup-players-modal-faction-image-faction-name">
                    {factionConfig.name}
                  </span>
                </div>
              );
            })}
        </div>
        <div className="setup-players-modal-footer">
          <PageButton
            className="setup-players-modal-skip"
            onClick={onSkip}
            noSound
          >
            Продолжить
          </PageButton>
        </div>
      </div>
    </>
  );
};

export default SetupPlayersModal;
